import React, { useEffect, useMemo, useState } from 'react';
import clientService from '../../../services/clientService';
import { Search, Calendar, Download } from 'lucide-react';

const PaymentsReports = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ paid_amount: 0, pending_amount: 0, overdue_amount: 0, total_payments: 0 });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await clientService.getPayments({ from_date: fromDate || undefined, to_date: toDate || undefined, status: status==='all'? undefined : status, per_page: 500 });
      setRows(res.data || []);
      const s = await clientService.getPaymentStatistics({ from_date: fromDate || undefined, to_date: toDate || undefined });
      setStats(s);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const exportCsv = () => {
    const header = ['Invoice','Client','Project','Amount','Payment Date','Due Date','Method','Status'];
    const lines = rows.map(r => [
      r.invoice_number,
      r.client?.company_name || '',
      r.project?.project_name || '',
      r.amount,
      r.payment_date,
      r.due_date || '',
      r.payment_method,
      r.status
    ].join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_report_${fromDate || 'all'}_${toDate || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-primary mb-2">Payments Reports</h1>
        <p className="text-gray-600">Filter and export payments</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="text-sm text-gray-600">From</label>
            <input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg"/>
          </div>
          <div>
            <label className="text-sm text-gray-600">To</label>
            <input type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg"/>
          </div>
          <div>
            <label className="text-sm text-gray-600">Status</label>
            <select value={status} onChange={(e)=>setStatus(e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg">
              {['all','paid','pending','overdue','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={load} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Apply</button>
            <button onClick={exportCsv} className="px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded-lg inline-flex items-center gap-2"><Download className="w-4 h-4"/> Export CSV</button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Total</p><p className="text-3xl font-bold text-primary mt-2">{stats.total_payments}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Paid</p><p className="text-3xl font-bold text-green-600 mt-2">฿{Number(stats.paid_amount||0).toLocaleString()}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Pending</p><p className="text-3xl font-bold text-orange-600 mt-2">฿{Number(stats.pending_amount||0).toLocaleString()}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Overdue</p><p className="text-3xl font-bold text-red-600 mt-2">฿{Number(stats.overdue_amount||0).toLocaleString()}</p></div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary-700">
          <h2 className="text-xl font-bold text-white">Payments ({rows.length})</h2>
        </div>
        {loading ? (<div className="p-10 text-center">Loading...</div>) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.length === 0 ? (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No payments</td></tr>
                ) : (
                  rows.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-semibold">{r.invoice_number}</td>
                      <td className="px-6 py-4">{r.client?.company_name || '—'}</td>
                      <td className="px-6 py-4">{r.project?.project_name || '—'}</td>
                      <td className="px-6 py-4">฿{Number(r.amount).toLocaleString()}</td>
                      <td className="px-6 py-4">{r.payment_date}</td>
                      <td className="px-6 py-4">{r.status}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsReports;
